import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MoreVertical, Pencil, Star, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { TryOnPhoto } from "@/api/models/api-types";
import { useTryOnPhotos } from "../hooks/use-try-on-photos";

interface TryOnPhotoCardProps {
  photo: TryOnPhoto;
}

/**
 * Tek bir try-on kartı. İsim doğrudan kart üzerinde düzenlenir;
 * `isSelf` fotoğrafı try-on için ana görseldir.
 */
export const TryOnPhotoCard: React.FC<TryOnPhotoCardProps> = ({ photo }) => {
  const { t } = useTranslation();
  const { renamePhoto, markAsSelf, openDeleteDialog, isUpdating } =
    useTryOnPhotos();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(photo.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) setDraft(photo.name);
  }, [photo.name, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [isEditing]);

  const startEditing = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setDraft(photo.name);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(photo.name);
    setIsEditing(false);
  };

  const commitEditing = () => {
    const name = draft.trim();
    setIsEditing(false);
    if (!name || name === photo.name) {
      setDraft(photo.name);
      return;
    }
    void renamePhoto(photo.id, name);
  };

  return (
    <div
      className={cn(
        "group relative min-w-0 aspect-[3/4] overflow-hidden rounded-xl border-2 bg-gray-100 dark:bg-gray-700",
        photo.isSelf
          ? "border-emerald-500 dark:border-emerald-400 shadow-[0_0_20px_-4px_rgba(16,185,129,0.5)]"
          : "border-transparent"
      )}
    >
      <img
        src={photo.url}
        alt={photo.name}
        loading="lazy"
        className="h-full w-full object-cover"
      />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/75 to-transparent" />

      <div className="absolute inset-x-2 bottom-2 flex flex-col items-center gap-1.5">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={draft}
            maxLength={40}
            disabled={isUpdating}
            aria-label={t("settings.profile.tryOn.renameLabel")}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commitEditing}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                event.currentTarget.blur();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                cancelEditing();
              }
            }}
            className="w-full rounded-md border border-white/40 bg-black/55 px-2 py-1 text-center text-xs font-medium text-white placeholder:text-white/60 outline-none focus:ring-2 focus:ring-white/60"
          />
        ) : (
          <button
            type="button"
            onClick={startEditing}
            title={t("settings.profile.tryOn.rename")}
            className="flex max-w-full items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-white transition-colors hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <span className="truncate">{photo.name}</span>
            <Pencil className="h-3 w-3 shrink-0 opacity-70" />
          </button>
        )}

        {photo.isSelf && (
          <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] sm:px-2.5 sm:py-1 sm:text-[11px] font-semibold text-white whitespace-nowrap shadow">
            <Star className="h-3 w-3 fill-current" />
            {t("settings.profile.tryOn.mainPhoto")}
          </span>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("settings.profile.tryOn.photoActions")}
            className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-lg bg-black/45 text-white backdrop-blur-sm transition-colors hover:bg-black/65 focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="dark:bg-gray-800 dark:border-gray-600"
        >
          {!photo.isSelf && (
            <DropdownMenuItem
              className="cursor-pointer gap-2 dark:text-gray-100 dark:focus:bg-gray-700"
              onSelect={() => void markAsSelf(photo.id)}
            >
              <Star className="h-4 w-4" />
              {t("settings.profile.tryOn.makeMain")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="cursor-pointer gap-2 dark:text-gray-100 dark:focus:bg-gray-700"
            onSelect={() => startEditing()}
          >
            <Pencil className="h-4 w-4" />
            {t("settings.profile.tryOn.rename")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer gap-2 text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:bg-gray-700"
            onSelect={() =>
              openDeleteDialog({ id: photo.id, name: photo.name })
            }
          >
            <Trash2 className="h-4 w-4" />
            {t("settings.profile.tryOn.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
